/** Super-admin OS health and explicit maintenance review. The server authorizes every
 * read/action; client visibility never grants permission. No operation is automatic. */
export function renderAdminOperations(ctx) {
  if (ctx.state?.isSuperAdmin !== true) return;
  const actor = ctx.state.me.id;
  const card = ctx.e('section', 'settings-card operations-admin');
  const status = ctx.e('p', 'subtle', 'Loading server health…');
  status.setAttribute('role', 'status');
  card.append(ctx.e('h2', '', 'Server health and storage'), status);
  ctx.view.append(card);
  let busy = false,
    revision = '',
    review;
  let pendingControls = [],
    generation = 0;
  const setBusy = (value) => {
    busy = value;
    if (value) {
      pendingControls = [...card.querySelectorAll('button,input')].map((node) => [
        node,
        node.disabled,
      ]);
      for (const [node] of pendingControls) node.disabled = true;
    } else {
      for (const [node, disabled] of pendingControls) node.disabled = disabled;
      pendingControls = [];
    }
  };
  const connected = () =>
    card.isConnected && ctx.state?.me.id === actor && ctx.state?.isSuperAdmin === true;
  const bytes = (n) => (n == null ? 'Unavailable' : `${(n / 1024 / 1024).toFixed(1)} MB`);
  const details = ctx.e('div', 'operations-summary');
  const inventory = ctx.e('div', 'operations-inventory');
  const actions = ctx.e('div', 'button-row');
  const confirmation = ctx.e('div', 'operations-review');
  card.append(details, inventory, actions, confirmation);
  const selected = () =>
    [...inventory.querySelectorAll('input:checked')].map((input) => ({
      kind: input.dataset.kind,
      name: input.value,
    }));
  async function load() {
    if (busy) return;
    setBusy(true);
    const current = ++generation;
    review = undefined;
    confirmation.replaceChildren();
    try {
      const value = await ctx.api('/api/admin/operations');
      if (!connected() || current !== generation) return;
      revision = value.inventory.revision;
      status.textContent = `Observed ${new Date(value.observedAt).toLocaleString()}. ${value.scope}`;
      details.replaceChildren();
      for (const [label, text] of [
        [
          'Host memory',
          `${bytes(value.host.freeMemoryBytes)} free / ${bytes(value.host.totalMemoryBytes)} total`,
        ],
        [
          'Host load',
          value.host.loadAverage.length
            ? value.host.loadAverage.map((n) => n.toFixed(2)).join(' / ')
            : 'Unavailable',
        ],
        ['Host CPUs', String(value.host.cpuCount)],
        [
          'Host uptime',
          value.host.uptimeSeconds == null
            ? 'Unavailable'
            : `${Math.floor(value.host.uptimeSeconds / 3600)} hours`,
        ],
        [
          'Filesystem',
          value.host.filesystem
            ? `${bytes(value.host.filesystem.freeBytes)} available / ${bytes(value.host.filesystem.totalBytes)} total`
            : 'Unavailable',
        ],
        ['Application memory', bytes(value.app.residentMemoryBytes)],
        [
          'App data / media blobs',
          `${bytes(value.app.dataBytes)} / ${bytes(value.app.uploadBytes)}`,
        ],
        [
          'Backups / releases',
          `${bytes(value.app.backupBytes)} / ${bytes(value.app.releaseBytes)}`,
        ],
      ])
        details.append(
          ctx.append(ctx.e('p'), ctx.e('strong', '', `${label}: `), ctx.e('span', '', text)),
        );
      for (const unit of value.units)
        details.append(
          ctx.e(
            'p',
            'subtle',
            `${unit.unit}: ${unit.available ? `${unit.active} · ${unit.restarts} restarts` : 'Service status unavailable'}`,
          ),
        );
      details.append(ctx.e('p', 'subtle', value.history));
      appendUXTimings(ctx, details, value.uxTimings);
      inventory.replaceChildren(ctx.e('h3', '', 'Cleanup inventory'));
      if (value.inventory.unavailable)
        inventory.append(
          ctx.e('p', 'subtle', 'Cleanup inventory is unavailable. Nothing can be selected.'),
        );
      for (const item of value.inventory.entries) {
        const input = ctx.field('input', {
          type: 'checkbox',
          value: item.name,
          disabled: item.protected || !value.operationsAvailable,
        });
        input.dataset.kind = item.kind;
        input.title = item.name;
        inventory.append(
          ctx.append(
            ctx.e('label', 'operations-item'),
            input,
            ctx.e(
              'span',
              '',
              `${item.kind} · ${item.name} · ${bytes(item.bytes)}${item.protected ? ` · ${item.reason}` : ''}`,
            ),
          ),
        );
      }
      actions.replaceChildren(
        ctx.button('Refresh health', load),
        ctx.button('Review cleanup', (node) => preview('cleanup', node)),
        ctx.button('Review backup now', (node) => preview('backup', node)),
      );
      for (const button of [...actions.children].slice(1))
        button.disabled = !value.operationsAvailable;
      if (!value.operationsAvailable)
        actions.append(
          ctx.e('p', 'subtle', 'Privileged operations are not installed on this host.'),
        );
      const history = ctx.e('div', 'operations-jobs');
      history.append(ctx.e('h3', '', 'Recent operations'));
      for (const job of value.jobs)
        history.append(
          ctx.e(
            'p',
            'subtle',
            `${new Date(job.createdAt).toLocaleString()} · ${job.status} · ${job.attempts} attempts${job.result ? ` · ${job.result}` : ''}`,
          ),
        );
      details.append(history);
    } catch (error) {
      if (connected() && current === generation) {
        revision = '';
        review = undefined;
        details.replaceChildren();
        inventory.replaceChildren();
        confirmation.replaceChildren();
        actions.replaceChildren(ctx.button('Retry health', load));
        status.textContent = error.message;
      }
    } finally {
      setBusy(false);
    }
  }
  async function preview(operation, trigger) {
    if (busy) return;
    setBusy(true);
    const current = ++generation;
    review = undefined;
    confirmation.replaceChildren();
    try {
      const value = await ctx.api('/api/admin/operations', {
        action: 'preview',
        operation,
        revision,
        items: operation === 'cleanup' ? selected() : [],
      });
      if (!connected() || current !== generation) return;
      review = value;
      const exact = ctx.e('div', 'operations-review-items');
      exact.append(
        ctx.e(
          'h3',
          '',
          operation === 'cleanup' ? 'Review permanent deletion' : 'Review backup request',
        ),
      );
      if (operation === 'cleanup')
        for (const item of value.items)
          exact.append(ctx.e('p', '', `${item.kind}: ${item.name} · ${bytes(item.bytes)}`));
      else
        exact.append(
          ctx.e(
            'p',
            '',
            'This queues the existing isolated myAudience backup service. Its configured retention policy may also remove older backups.',
          ),
        );
      exact.append(
        ctx.e(
          'p',
          'subtle',
          `Review expires ${new Date(value.expiresAt).toLocaleTimeString()}. Cleanup cannot be undone; backups and active releases remain protected.`,
        ),
      );
      const confirm = ctx.field('input', { type: 'checkbox' });
      const submit = ctx.button(
        operation === 'cleanup' ? 'Confirm deletion' : 'Confirm backup',
        async () => {
          if (busy || !confirm.checked || review !== value) return;
          setBusy(true);
          submit.disabled = true;
          try {
            const response = await ctx.api('/api/admin/operations', {
              action: 'confirm',
              previewId: value.previewId,
              requestId: (value.requestId ??= crypto.randomUUID()),
            });
            if (!connected() || current !== generation) return;
            confirmation.replaceChildren(
              ctx.e(
                'p',
                'subtle',
                `Operation queued: ${response.jobId}. Refresh health to check the result.`,
              ),
            );
            review = undefined;
          } catch (error) {
            if (connected()) status.textContent = error.message;
            submit.disabled = !confirm.checked;
          } finally {
            setBusy(false);
          }
        },
        operation === 'cleanup' ? 'danger-button' : 'primary-button',
      );
      submit.disabled = true;
      confirm.addEventListener('change', () => {
        submit.disabled = !confirm.checked;
      });
      exact.append(
        ctx.append(ctx.e('label'), confirm, ctx.e('span', '', 'I reviewed this exact operation.')),
        submit,
        ctx.button('Cancel review', () => {
          if (busy) return;
          generation++;
          review = undefined;
          confirmation.replaceChildren();
          (trigger ?? actions.children[operation === 'cleanup' ? 1 : 2])?.focus();
        }),
      );
      confirmation.append(exact);
      confirm.focus();
    } catch (error) {
      if (connected()) status.textContent = error.message;
    } finally {
      setBusy(false);
    }
  }
  void load();
}

/** Aggregate-only measurements; no member drilldown or invented Web Vitals score. */
function appendUXTimings(ctx, host, value) {
  const section = ctx.e('section', 'operations-ux-timings');
  section.append(ctx.e('h3', '', 'App load timings'));
  if (!value) {
    section.append(ctx.e('p', 'subtle', 'Timing metrics unavailable.'));
    host.append(section);
    return;
  }
  section.append(ctx.e('p', 'subtle', `${value.retentionHours / 24}-day window. ${value.scope}`));
  if (!value.enabled)
    section.append(
      ctx.e('p', 'subtle', 'New timing reports are disabled. Retained aggregates remain visible.'),
    );
  if (!value.groups.length) section.append(ctx.e('p', 'subtle', 'No timing reports yet.'));
  else {
    const table = ctx.e('table');
    table.append(
      ctx.append(
        ctx.e('caption'),
        ctx.e(
          'span',
          '',
          'Approximate p50/p95 upper bounds in milliseconds; fewer than five reports show —.',
        ),
      ),
    );
    const heading = ctx.e('tr');
    for (const label of [
      'Milestone / surface',
      'Reports',
      'Observed range ms',
      'p50 ms',
      'p95 ms',
    ]) {
      const cell = ctx.e('th', '', label);
      cell.setAttribute('scope', 'col');
      heading.append(cell);
    }
    table.append(ctx.append(ctx.e('thead'), heading));
    const body = ctx.e('tbody');
    const names = {
      'first-screen-request': 'First screen request',
      'first-screen-ready': 'First screen DOM ready',
      'first-image-ready': 'First image ready',
      'surface-switch-render': 'Surface switch render',
      'load-more': 'Next page wait',
    };
    for (const item of value.groups) {
      const row = ctx.e('tr');
      const label = ctx.e('th', '', `${names[item.kind] ?? item.kind} / ${item.surface}`);
      label.setAttribute('scope', 'row');
      row.append(label);
      row.append(ctx.e('td', '', String(item.count)));
      row.append(
        ctx.e(
          'td',
          '',
          item.observedLowerMs == null || item.observedUpperMs == null
            ? '—'
            : `${item.observedLowerMs}–${item.observedUpperMs}`,
        ),
      );
      for (const n of [item.p50Ms, item.p95Ms])
        row.append(ctx.e('td', '', n == null ? '—' : String(n)));
      body.append(row);
    }
    table.append(body);
    section.append(table);
  }
  host.append(section);
}
